import { useEffect, useRef, useState } from 'react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { TextConsentCheckbox } from '../../components/TextReminders';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { StatusBanner } from '../../components/StatusBanner';
import { computeProfileProgress } from '../../lib/profile';
import { formatPhone, PHONE_MASK } from '../../lib/format';
import { DateField } from '../../components/DateField';
import type {
  PersonalInfo,
  PilotInfo,
  Disclosures,
  EmergencyContact,
  InsuranceInfo,
} from '../../lib/types';

const EMPTY_EC: EmergencyContact = { name: '', relationship: '', phone: '' };

const EMPTY_INSURANCE: InsuranceInfo = {
  carrier: '',
  policyNumber: '',
  liabilityLimit: 0,
  expirationDate: '',
};

const EMPTY_PERSONAL: PersonalInfo = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  address: '',
  phone: '',
  emergencyContact: EMPTY_EC,
};

const EMPTY_PILOT: PilotInfo = {
  certificateType: '',
  certificateNumber: '',
  certificateIssueDate: '',
  ratings: [],
  medicalClass: '',
  medicalIssueDate: '',
  medicalExpiration: '',
  medicalLimitations: '',
  lastBFR: '',
  bfrExpiration: '',
  lastIPC: '',
  ipcExpiration: '',
  totalHours: 0,
  hoursIn172: 0,
  hoursLast90: 0,
  hoursLast12Months: 0,
  intendedLeaseDuration: '',
  flyingGoal: '',
};

const EMPTY_DISC: Disclosures = {
  priorAccident: { answer: '', explanation: '' },
  priorEnforcement: { answer: '', explanation: '' },
  priorInsuranceRefusal: { answer: '', explanation: '' },
  dui: { answer: '', explanation: '' },
  pendingActions: { answer: '', explanation: '' },
};

const RATING_OPTIONS = [
  'Single-Engine Land',
  'Single-Engine Sea',
  'Multi-Engine Land',
  'Instrument Airplane',
  'Commercial',
  'CFI',
  'CFII',
  'MEI',
  'ATP',
  'Tailwheel endorsement',
  'High-performance endorsement',
  'Complex endorsement',
];

export function Profile() {
  const { profile, fbUser } = useAuth();
  const [personal, setPersonal] = useState<PersonalInfo>(EMPTY_PERSONAL);
  const [pilot, setPilot] = useState<PilotInfo>(EMPTY_PILOT);
  const [insurance, setInsurance] = useState<InsuranceInfo>(EMPTY_INSURANCE);
  const [disclosures, setDisclosures] = useState<Disclosures>(EMPTY_DISC);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const savedSnapshot = useRef('');

  // Already submitted/onboarded → background answers lock; saving = "Save changes".
  const submitted = !!profile && profile.status !== 'profile_incomplete';

  useEffect(() => {
    if (!profile) return;
    const p = {
      ...EMPTY_PERSONAL,
      ...(profile.personal as PersonalInfo),
      emergencyContact: { ...EMPTY_EC, ...(profile.personal?.emergencyContact as EmergencyContact) },
    };
    const pi = { ...EMPTY_PILOT, ...(profile.pilot as PilotInfo) };
    const ins = { ...EMPTY_INSURANCE, ...(profile.insurance as InsuranceInfo) };
    const disc = { ...EMPTY_DISC, ...(profile.disclosures as Disclosures) };
    setPersonal(p);
    setPilot(pi);
    setInsurance(ins);
    setDisclosures(disc);
    savedSnapshot.current = JSON.stringify({ p, pi, ins, disc });
  }, [profile]);

  const dirty = JSON.stringify({ p: personal, pi: pilot, ins: insurance, disc: disclosures }) !== savedSnapshot.current;

  const progress = computeProfileProgress({
    ...(profile ?? ({} as never)),
    personal,
    pilot,
    insurance,
    disclosures,
  } as never);

  async function save(submitForVerification = false) {
    if (!fbUser || !db) return;
    setSaving(true);
    setMsg(null);
    try {
      const patch: Record<string, unknown> = { personal, pilot, insurance };
      // Background answers are write-once: only sent before submission.
      if (!submitted) patch.disclosures = disclosures;
      if (submitForVerification) {
        patch.status = 'verification_pending';
        patch.submittedForVerificationAt = serverTimestamp();
      }
      await updateDoc(doc(db, 'users', fbUser.uid), patch);
      savedSnapshot.current = JSON.stringify({ p: personal, pi: pilot, ins: insurance, disc: disclosures });
      setMsg({ kind: 'ok', text: submitForVerification ? 'Profile submitted for verification.' : 'Changes saved.' });
    } catch (e) {
      setMsg({
        kind: 'err',
        text: e instanceof Error ? e.message : 'Could not save profile.',
      });
    } finally {
      setSaving(false);
    }
  }

  function toggleRating(r: string) {
    setPilot((p) => ({
      ...p,
      ratings: p.ratings.includes(r) ? p.ratings.filter((x) => x !== r) : [...p.ratings, r],
    }));
  }

  return (
    <>
      <PageHeader
        title="Your profile"
        subtitle="Required for booking and flight logging."
        actions={
          <button disabled={saving || !dirty} onClick={() => save(false)} className="btn-secondary">
            {saving ? 'Saving…' : dirty ? (submitted ? 'Save changes' : 'Save draft') : 'Saved'}
          </button>
        }
      />

      {!submitted && (
        <StatusBanner
          tone={progress.pct === 100 ? 'success' : 'info'}
          title={`Profile ${progress.pct}% complete`}
        >
          Complete every section and submit for verification. Document uploads are on the Documents tab.
        </StatusBanner>
      )}

      <div className="mt-6 space-y-6">
        <Section title="Personal information">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" value={personal.firstName} onChange={(v) => setPersonal({ ...personal, firstName: v })} />
            <Field label="Last name" value={personal.lastName} onChange={(v) => setPersonal({ ...personal, lastName: v })} />
            <Field label="Date of birth" type="date" value={personal.dateOfBirth} onChange={(v) => setPersonal({ ...personal, dateOfBirth: v })} />
            <Field label="Phone" type="tel" placeholder={PHONE_MASK} value={personal.phone} onChange={(v) => setPersonal({ ...personal, phone: formatPhone(v) })} />
            <Field className="sm:col-span-2" label="Home address" value={personal.address} onChange={(v) => setPersonal({ ...personal, address: v })} />
          </div>
        </Section>

        <Section title="Text reminders">
          <TextConsentCheckbox source="profile" />
        </Section>

        <Section title="Emergency contact">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Name" value={personal.emergencyContact.name} onChange={(v) => setPersonal({ ...personal, emergencyContact: { ...personal.emergencyContact, name: v } })} />
            <Field label="Relationship" value={personal.emergencyContact.relationship} onChange={(v) => setPersonal({ ...personal, emergencyContact: { ...personal.emergencyContact, relationship: v } })} />
            <Field label="Phone" type="tel" placeholder={PHONE_MASK} value={personal.emergencyContact.phone} onChange={(v) => setPersonal({ ...personal, emergencyContact: { ...personal.emergencyContact, phone: formatPhone(v) } })} />
          </div>
        </Section>

        <Section title="Pilot certificate">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Certificate type" placeholder="Private, Commercial, ATP…" value={pilot.certificateType} onChange={(v) => setPilot({ ...pilot, certificateType: v })} />
            <Field label="Certificate number" value={pilot.certificateNumber} onChange={(v) => setPilot({ ...pilot, certificateNumber: v })} />
            <Field label="Issue date" type="date" value={pilot.certificateIssueDate} onChange={(v) => setPilot({ ...pilot, certificateIssueDate: v })} />
          </div>
          <div className="mt-4">
            <span className="label">Ratings &amp; endorsements</span>
            <div className="flex flex-wrap gap-2">
              {RATING_OPTIONS.map((r) => {
                const on = pilot.ratings.includes(r);
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => toggleRating(r)}
                    className={`px-3 py-1.5 rounded-full text-xs ring-1 ring-inset transition ${
                      on
                        ? 'bg-brand text-white ring-brand'
                        : 'bg-white text-navy-900 ring-navy-900/15 hover:bg-sky-100'
                    }`}
                  >
                    {r}
                  </button>
                );
              })}
            </div>
          </div>
        </Section>

        <Section title="Flight time">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label="Total hours" type="number" value={String(pilot.totalHours || '')} onChange={(v) => setPilot({ ...pilot, totalHours: Number(v) || 0 })} />
            <Field label="Hours in C172" type="number" value={String(pilot.hoursIn172 || '')} onChange={(v) => setPilot({ ...pilot, hoursIn172: Number(v) || 0 })} />
            <Field label="Last 90 days" type="number" value={String(pilot.hoursLast90 || '')} onChange={(v) => setPilot({ ...pilot, hoursLast90: Number(v) || 0 })} />
            <Field label="Last 12 months" type="number" value={String(pilot.hoursLast12Months || '')} onChange={(v) => setPilot({ ...pilot, hoursLast12Months: Number(v) || 0 })} />
          </div>
        </Section>

        <Section title="Medical certificate">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="label">Class</label>
              <select className="input" value={pilot.medicalClass} onChange={(e) => setPilot({ ...pilot, medicalClass: e.target.value as PilotInfo['medicalClass'] })}>
                <option value="">Select…</option>
                <option value="1st">1st Class</option>
                <option value="2nd">2nd Class</option>
                <option value="3rd">3rd Class</option>
                <option value="basicmed">BasicMed</option>
              </select>
            </div>
            <Field label="Issue date" type="date" value={pilot.medicalIssueDate} onChange={(v) => setPilot({ ...pilot, medicalIssueDate: v })} />
            <Field label="Expiration" type="date" value={pilot.medicalExpiration} onChange={(v) => setPilot({ ...pilot, medicalExpiration: v })} />
          </div>
          <div className="mt-4">
            <label className="label">Limitations (if any)</label>
            <textarea className="input-textarea" value={pilot.medicalLimitations} onChange={(e) => setPilot({ ...pilot, medicalLimitations: e.target.value })} />
          </div>
        </Section>

        <Section title="Renter’s insurance">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Insurance carrier" value={insurance.carrier} onChange={(v) => setInsurance({ ...insurance, carrier: v })} />
            <Field label="Policy number" value={insurance.policyNumber} onChange={(v) => setInsurance({ ...insurance, policyNumber: v })} />
            <Field label="Liability limit (USD)" type="number" value={String(insurance.liabilityLimit || '')} onChange={(v) => setInsurance({ ...insurance, liabilityLimit: Number(v) || 0 })} />
            <Field label="Policy expiration" type="date" value={insurance.expirationDate} onChange={(v) => setInsurance({ ...insurance, expirationDate: v })} />
            <Field label="Policy effective date" type="date" value={insurance.effectiveDate ?? ''} onChange={(v) => setInsurance({ ...insurance, effectiveDate: v })} />
            <Field label="Per-passenger limit (USD)" type="number" value={String(insurance.passengerLimit || '')} onChange={(v) => setInsurance({ ...insurance, passengerLimit: Number(v) || 0 })} />
            <Field label="Damage to non-owned aircraft limit (USD)" type="number" value={String(insurance.aircraftDamageLimit || '')} onChange={(v) => setInsurance({ ...insurance, aircraftDamageLimit: Number(v) || 0 })} />
          </div>
        </Section>

        <Section title="Background disclosures">
          {submitted && (
            <div className="mb-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-ink-muted ring-1 ring-slate-900/[0.06]">
              🔒 Your background answers were locked when you submitted. Contact the owner if any of these need to change.
            </div>
          )}
          <div className="space-y-4">
            {(Object.keys(disclosures) as Array<keyof Disclosures>).map((key) => (
              <DisclosureRow
                key={key}
                label={DISCLOSURE_LABELS[key]}
                value={disclosures[key]}
                locked={submitted}
                onChange={(v) => setDisclosures({ ...disclosures, [key]: v })}
              />
            ))}
          </div>
        </Section>

        {msg && (
          <div
            role="alert"
            className={`text-sm rounded-md px-3 py-2 ${
              msg.kind === 'ok'
                ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}
          >
            {msg.text}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-3 sticky bottom-0 bg-canvas/95 backdrop-blur py-4 -mx-4 sm:mx-0 px-4 sm:px-0 border-t border-navy-900/10">
          {submitted ? (
            <>
              <span className="text-sm text-ink-muted">
                {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
              </span>
              <button
                type="button"
                onClick={() => save(false)}
                disabled={saving || !dirty}
                className="btn-primary"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => save(false)} disabled={saving || !dirty} className="btn-secondary">
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button
                type="button"
                onClick={() => save(true)}
                disabled={saving || progress.pct < 100}
                className="btn-primary"
                title={progress.pct < 100 ? 'Complete every section first.' : 'Submit for owner verification.'}
              >
                Submit for verification
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
}

const DISCLOSURE_LABELS: Record<keyof Disclosures, string> = {
  priorAccident: 'Have you been involved in any prior aircraft accident or incident?',
  priorEnforcement: 'Have you ever been the subject of any FAA enforcement action?',
  priorInsuranceRefusal:
    'Has any aviation insurer ever refused, cancelled, or non-renewed coverage for you?',
  dui: 'Any DUI / drug or alcohol-related convictions in the last 10 years?',
  pendingActions: 'Are there any pending FAA actions or investigations against you?',
};

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5 sm:p-6">
      <h2 className="font-display text-lg font-semibold text-navy-900 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  className = '',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="label">{label}</label>
      {type === 'date' || type === 'datetime-local' ? (
        <DateField
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input
          className="input"
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function DisclosureRow({
  label,
  value,
  onChange,
  locked,
}: {
  label: string;
  value: { answer: 'yes' | 'no' | ''; explanation: string };
  onChange: (v: { answer: 'yes' | 'no' | ''; explanation: string }) => void;
  locked?: boolean;
}) {
  return (
    <div className={`border-t border-slate-100 pt-4 first:border-t-0 first:pt-0 ${locked ? 'opacity-90' : ''}`}>
      <div className="font-medium text-ink">{label}</div>
      <div className="mt-2 flex gap-3">
        {(['yes', 'no'] as const).map((v) => (
          <label key={v} className={`inline-flex items-center gap-2 text-sm ${locked ? 'cursor-default' : ''}`}>
            <input
              type="radio"
              checked={value.answer === v}
              disabled={locked}
              onChange={() => onChange({ ...value, answer: v })}
            />
            <span className="capitalize">{v}</span>
          </label>
        ))}
      </div>
      {value.answer === 'yes' && (
        <textarea
          className="input-textarea mt-3"
          placeholder="Please explain…"
          value={value.explanation}
          disabled={locked}
          onChange={(e) => onChange({ ...value, explanation: e.target.value })}
        />
      )}
    </div>
  );
}
